/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useCallback, useState } from 'react';
import { useDeviceInfo } from '@airasia-rn/ui';
import type { CurrencyItem } from '@airasia-rn/ui/widgets';
import type { MemoizedListViewProps } from '@airasia-phoenix/react-native/components/SearchableListPageView/MemoizedListView';
import LocaleCurrencyMobilePage from '../../pages/LocaleCurrencyMobilePage/LocaleCurrencyMobilePage';
import LocaleCurrencyModal from '../../components/LocaleCurrencyModal/LocaleCurrencyModal';
import type { LocaleCurrencyModalProps } from '../../components/LocaleCurrencyModal/LocaleCurrencyModal';
import useLocaleCurrencyAnalytics from './useLocaleCurrencyAnalytics';

export type LocaleCurrencyViewProps = LocaleCurrencyModalProps;

const LocaleCurrencyView: React.FC<LocaleCurrencyViewProps> = ({
  visible,
  defaultTab = 'currency',
  onCurrencySelect,
  onLanguageSelect,
  selectedCurrency,
  selectedLanguage,
  ...rest
}) => {
  const { isMobileWidth } = useDeviceInfo();
  const [activeTab, setActiveTab] = useState<'currency' | 'language'>(
    defaultTab,
  );
  const [prevVisible, setPrevVisible] = useState(visible);
  if (visible !== prevVisible) {
    setPrevVisible(visible);
    if (visible) setActiveTab(defaultTab);
  }

  const { trackCurrencySelection, trackLanguageSelection } =
    useLocaleCurrencyAnalytics({
      selectedCurrencyId: selectedCurrency.id,
      selectedLanguageId: selectedLanguage.id,
    });

  const handleCurrencySelect = useCallback(
    (item: CurrencyItem) => {
      trackCurrencySelection(item);
      onCurrencySelect(item);
    },
    [onCurrencySelect, trackCurrencySelection],
  );

  const handleLanguageSelect = useCallback(
    (item: MemoizedListViewProps) => {
      trackLanguageSelection(item);
      onLanguageSelect(item);
    },
    [onLanguageSelect, trackLanguageSelection],
  );

  const sharedProps = {
    ...rest,
    visible,
    defaultTab: activeTab,
    onTabChange: setActiveTab,
    selectedCurrency,
    selectedLanguage,
    onCurrencySelect: handleCurrencySelect,
    onLanguageSelect: handleLanguageSelect,
  };

  if (isMobileWidth) {
    return <LocaleCurrencyMobilePage {...sharedProps} />;
  }

  return <LocaleCurrencyModal {...sharedProps} />;
};

export default LocaleCurrencyView;
